/// <amd-module name="Wit/BoardsHub/KanbanBoard/ColumnNavigationGraph" />
import { BoardColumnType } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { BoardColumnLayout } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { hasItems, isIncoming, isOutgoing } from "Wit/BoardsHub/KanbanBoard/Utils/BoardUtils";

interface IColumnGraphNode {
    memberType: BoardColumnType;
    aboveNodeIndex: number;
    belowNodeIndex: number;
    rightNodeIndex: number;
    leftNodeIndex: number;
}

export class ColumnNavigationGraph {
    private graphNodes: IColumnGraphNode[];
    private columns: BoardColumnLayout[];
    private numberOfColumnsInEachLane: number;
    private filteredIds?: Set<number>;

    constructor(columns: BoardColumnLayout[], numberOfRowsInBoard: number, filteredIds?: Set<number>) {
        this.initializeGraph(columns, numberOfRowsInBoard, filteredIds);
    }

    public reconstructGraph(columns: BoardColumnLayout[], numberOfRowsInBoard: number, filteredIds?: Set<number>): void {
        this.initializeGraph(columns, numberOfRowsInBoard, filteredIds);
    }

    private initializeGraph(columns: BoardColumnLayout[], numberOfRowsInBoard: number, filteredIds?: Set<number>): void {
        this.columns = columns;
        this.filteredIds = filteredIds;
        this.numberOfColumnsInEachLane = (this.columns.length - 2) / numberOfRowsInBoard; // -2 to exclude incoming and outgoing
        this.graphNodes = [];

        this.constructColumnGraph();
    }

    /**
     *  Visual representation of the member navigation graph constructed from the leaf member views of the board
     *
     *    Sample set: A board with 2 swimlanes and 3 columns in each swimlane
     *
     *    - Leaf member views from the board in the same order as in the board level tree structure, stored in array
     *
     *    |'''|   |'''|   |'''|   |'''|   |'''|   |'''|   |'''|   |'''|                      0   - Member view of type "Incoming"
     *    | 0 |   | 1 |   | 2 |   | 3 |   | 4 |   | 5 |   | 6 |   | 7 |                    (1-6) - Member views of type "Inprogress"
     *    |   |   |   |   |   |   |   |   |   |   |   |   |   |   |   |                      7   - Member view of type "Outgoing"
     *    '''''   '''''   '''''   '''''   '''''   '''''   '''''   '''''
     *
     *    - Structure of the graph that is constructed using 4 indices per node - [top, bottom, right and left]
     *
     *               -1        -1        -1        -1        -1
     *                ^         ^         ^         ^         ^
     *                |         |         |         |         |
     *                |         |         |         |         |
     *              |'''|     |'''|     |'''|     |'''|     |'''|
     *              |   | <-- | 1 | --> | 2 | --> | 3 | <-- |   |
     *              |   | --> |   | <-- |   | <-- |   | --> |   |
     *              |   |     '''''     '''''     '''''     |   |
     *              |   |      ^ |       ^ |       ^ |      |   |                          (1-3) - Makes the first lane
     *       -1 <-- | 0 |      | |       | |       | |      | 7 | --> -1                   (4-6) - Makes the second lane
     *              |   |      | v       | v       | v      |   |
     *              |   |                                   |   |
     *              |   |     |'''|     |'''|     |'''|     |   |
     *              |   | <-- | 4 | --> | 5 | --> | 6 | --> |   |
     *              |   |     |   | <-- |   | <-- |   |     |   |
     *              '''''     '''''     '''''     '''''     '''''
     *                |         |         |         |         |
     *                |         |         |         |         |
     *                v         v         v         v         v
     *                -1        -1        -1        -1        -1
     *
     *
     */
    private constructColumnGraph(): void {
        this.columns.forEach((column: BoardColumnLayout, index: number) => {
            if (column.columnType === BoardColumnType.Incoming) {
                // Incoming Member is at index 0
                this.graphNodes.push({
                    memberType: BoardColumnType.Incoming,
                    aboveNodeIndex: -1,
                    belowNodeIndex: -1,
                    rightNodeIndex: index + 1,
                    leftNodeIndex: -1
                });
            } else if (column.columnType === BoardColumnType.Outgoing) {
                // Outgoing Member is at last index
                this.graphNodes.push({
                    memberType: BoardColumnType.Outgoing,
                    aboveNodeIndex: -1,
                    belowNodeIndex: -1,
                    rightNodeIndex: -1,
                    leftNodeIndex: this.numberOfColumnsInEachLane
                });
            } else {
                // InProgress Members folded including members in swimlanes
                this.graphNodes.push({
                    memberType: BoardColumnType.InProgress,
                    aboveNodeIndex: index > this.numberOfColumnsInEachLane ? index - this.numberOfColumnsInEachLane : -1,
                    belowNodeIndex: index + this.numberOfColumnsInEachLane < this.columns.length - 1 ? index + this.numberOfColumnsInEachLane : -1,
                    rightNodeIndex: index % this.numberOfColumnsInEachLane == 0 ? this.columns.length - 1 : index + 1,
                    leftNodeIndex: index % this.numberOfColumnsInEachLane == 1 ? 0 : index - 1
                });
            }
        });
    }

    /**
     *  Returns the next(right) member view for the provided member view, in the visual board
     */
    public getNextColumn(column?: BoardColumnLayout, isNonEmptyMember?: boolean): BoardColumnLayout | undefined {
        if (!column) {
            return;
        }

        const currentIndex = this.getColumnIndex(column.id, column.rowId, column.isDone);
        if (isNonEmptyMember !== undefined && isNonEmptyMember === true) {
            if (isOutgoing(column)) {
                return;
            } else if (isIncoming(column)) {
                return this.firstNonEmptyRightSiblingOfIncoming(column);
            } else {
                return this.firstNonEmptySiblingOfInProgress(column, true);
            }
        } else {
            const siblingIndex: number = this.graphNodes[currentIndex].rightNodeIndex;
            if (siblingIndex >= 0) {
                return this.columns[siblingIndex];
            }
        }
    }

    /**
     *  Returns the previous(left) member view for the provided member view, in the visual boar
     */
    public getPreviousColumn(column?: BoardColumnLayout, isNonEmptyMember?: boolean): BoardColumnLayout | undefined {
        if (!column) {
            return;
        }

        const currentIndex = this.getColumnIndex(column.id, column.rowId, column.isDone);
        if (isNonEmptyMember !== undefined && isNonEmptyMember === true) {
            if (isIncoming(column)) {
                return;
            } else if (isOutgoing(column)) {
                return this.firstNonEmptyLeftSiblingOfOutgoing(column);
            } else {
                return this.firstNonEmptySiblingOfInProgress(column, false);
            }
        } else {
            const siblingIndex: number = this.graphNodes[currentIndex].leftNodeIndex;
            if (siblingIndex >= 0) {
                return this.columns[siblingIndex];
            }
        }
    }

    /**
     *  Returns the above member view for the provided member view, in the visual board
     */
    public getAboveColumn(column?: BoardColumnLayout, isNonEmptyMember?: boolean): BoardColumnLayout | undefined {
        if (!column) {
            return;
        }

        const currentIndex = this.getColumnIndex(column.id, column.rowId, column.isDone);
        const siblingIndex: number = this.graphNodes[currentIndex].aboveNodeIndex;
        if (siblingIndex >= 0) {
            let aboveMember: BoardColumnLayout | undefined = this.columns[siblingIndex];
            if (isNonEmptyMember) {
                while (aboveMember) {
                    if (hasItems(aboveMember, this.filteredIds)) {
                        return aboveMember;
                    } else {
                        const nonEmptySibling = this.getFirstNonEmptySiblingMemberInLane(aboveMember);
                        if (nonEmptySibling) {
                            return nonEmptySibling;
                        }
                    }

                    aboveMember = this.getAboveColumn(aboveMember);
                }
            }

            return aboveMember;
        }
    }

    /**
     *  Returns the below member view for the provided member view, in the visual board
     */
    public getBelowColumn(column?: BoardColumnLayout, isNonEmptyMember?: boolean): BoardColumnLayout | undefined {
        if (!column) {
            return;
        }

        const currentIndex = this.getColumnIndex(column.id, column.rowId, column.isDone);
        const siblingIndex: number = this.graphNodes[currentIndex].belowNodeIndex;
        if (siblingIndex >= 0) {
            let belowMember: BoardColumnLayout | undefined = this.columns[siblingIndex];
            if (isNonEmptyMember) {
                while (belowMember) {
                    if (hasItems(belowMember, this.filteredIds)) {
                        return belowMember;
                    } else {
                        const nonEmptySibling = this.getFirstNonEmptySiblingMemberInLane(belowMember);
                        if (nonEmptySibling) {
                            return nonEmptySibling;
                        }
                    }

                    belowMember = this.getBelowColumn(belowMember);
                }
            }

            return belowMember;
        }
    }

    /**
     *  Returns the first non-empty sibling member in the lane for the given member view. Does not traverse beyond the lane.
     *  Traverses to the right and left of the given member, one step at a time, using the graph
     *  If both right and left non-empty members are at the same displacement from the column, the right sibling is returned
     */
    private getFirstNonEmptySiblingMemberInLane(column?: BoardColumnLayout): BoardColumnLayout | undefined {
        if (!column) {
            return;
        }

        // Assert if the current member is of InProgress
        if (isIncoming(column) || isOutgoing(column)) {
            return;
        }

        const currentIndex = this.getColumnIndex(column.id, column.rowId);
        const currentType = this.graphNodes[currentIndex].memberType;
        let leftMember = this.getPreviousColumn(column);
        let rightMember = this.getNextColumn(column);
        while (leftMember || rightMember) {
            if (rightMember) {
                if (this.graphNodes[this.getColumnIndex(rightMember.id, rightMember.rowId)].memberType !== currentType) {
                    rightMember = undefined;
                } else {
                    if (hasItems(rightMember, this.filteredIds)) {
                        return rightMember;
                    }

                    rightMember = this.getNextColumn(rightMember);
                }
            }

            if (leftMember) {
                if (this.graphNodes[this.getColumnIndex(leftMember.id, leftMember.rowId)].memberType !== currentType) {
                    leftMember = undefined;
                } else {
                    if (hasItems(leftMember, this.filteredIds)) {
                        return leftMember;
                    }

                    leftMember = this.getPreviousColumn(leftMember);
                }
            }
        }
    }

    /**
     *  Returns the first non-empty sibling member for a member view of type "Inprogress"
     */
    private firstNonEmptySiblingOfInProgress(column?: BoardColumnLayout, rightSibling?: boolean): BoardColumnLayout | undefined {
        if (!column || isIncoming(column) || isOutgoing(column)) {
            return;
        }

        if (rightSibling) {
            let rightMember = this.getNextColumn(column);
            while (rightMember) {
                if (hasItems(rightMember, this.filteredIds)) {
                    return rightMember;
                }

                rightMember = this.getNextColumn(rightMember);
            }
        } else {
            let leftMember = this.getPreviousColumn(column);
            while (leftMember) {
                if (hasItems(leftMember, this.filteredIds)) {
                    return leftMember;
                }

                leftMember = this.getPreviousColumn(leftMember);
            }
        }
    }

    /**
     *  Returns the first non-empty right sibling member for a member view of type "Incoming"
     */
    private firstNonEmptyRightSiblingOfIncoming(column?: BoardColumnLayout): BoardColumnLayout | undefined {
        if (!column || !isIncoming(column)) {
            return;
        }

        // Traversing both Inprogress and Outgoing in sequence
        let siblingIndex = this.getColumnIndex(column.id, column.rowId);
        while (++siblingIndex <= this.columns.length - 1) {
            const sibling: BoardColumnLayout = this.columns[siblingIndex];
            if (sibling && hasItems(sibling, this.filteredIds)) {
                return sibling;
            }
        }
    }

    /**
     *  Returns the first non-empty left sibling member for a member view of type "Outgoing"
     */
    private firstNonEmptyLeftSiblingOfOutgoing(column: BoardColumnLayout): BoardColumnLayout | undefined {
        if (!column) {
            return;
        }

        // Assert if the current member is of Outcoming
        if (!isOutgoing(column)) {
            return;
        }

        let siblingIndex = 0;
        // Traversing Inprogress first
        while (++siblingIndex < this.columns.length - 1) {
            let sibling = this.columns[siblingIndex];
            if (!sibling || !hasItems(sibling, this.filteredIds)) {
                continue;
            }

            let rightMember = this.getNextColumn(sibling);
            while (rightMember) {
                if (hasItems(rightMember, this.filteredIds)) {
                    break;
                }

                rightMember = this.getNextColumn(rightMember);
            }

            if (rightMember === column) {
                return sibling;
            }
        }

        // Traverse Incoming when there are no non-empty members in Inprogress
        const sibling: BoardColumnLayout = this.columns[0];
        if (sibling && hasItems(sibling, this.filteredIds)) {
            return sibling;
        }
    }

    private getColumnIndex(columnId: string, rowId?: string, isDone?: boolean) {
        return this.columns.findIndex(column => column.id === columnId && column.rowId === rowId && column.isDone === isDone);
    }
}
